import { AppModalForm } from 'components/organisms';
import {
    AppModal,
    DayPeriodPickerElement,
    FormHeader,
    FormNavButtons,
    SimpleInputElement,
    VatRatePickerElement,
    SimpleDatePicker,
} from 'components/molecules';
import { HeaderContainer } from 'components/organisms/AppForm/AppForm.styled';
import { commonTexts, settlementTexts } from 'consts/text';
import { AppFormItem } from 'components/atoms';
import { formFields } from 'consts/form/formFields';
import { CurrencyInputElement } from 'components/molecules/FormElements/CurrencyInputElement/CurrencyInputElement';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import {
    ChildId,
    CommonFormProps,
    FormPageModeDependedTexts,
    ManualBillCostCategory,
    VatRateType,
    VatReleaseReason,
} from 'types';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';

import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useEffect } from 'react';
import { useWatch } from 'antd/es/form/Form';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';
import { max300LengthRule, max300LengthRuleOptional } from 'consts/rules/textRules';
import { AddManualBillFormFields } from 'types/forms/addManualBill';
import { If } from '../../../atoms/If/If';
import { VatReleaseReasonPickerElement } from '../../../molecules/FormElements/VatReleaseReasonPickerElement/VatReleaseReasonPickerElement';

type ChildAddBillModalType = {
    isOpen: boolean;
    childId?: ChildId | null;
    additionalCostId?: string | null;
    onCancel: () => void;
} & Pick<CommonFormProps, 'mode' | 'initialValues'>;

const addModeTexts: FormPageModeDependedTexts = {
    title: settlementTexts.children.addManualBill.title,
    buttonText: commonTexts.actionLabels.add,
};

const editModeTexts: FormPageModeDependedTexts = {
    title: settlementTexts.children.editManualBill.title,
    buttonText: commonTexts.actionLabels.save,
    buttonIcon: <IconDeviceFloppy size={20} />,
};

export const AddEditAdditionalCostModal = ({
    isOpen,
    childId,
    additionalCostId,
    onCancel,
    mode = 'add',
}: ChildAddBillModalType) => {
    const isAddMode = mode === 'add';

    const { title, buttonText, buttonIcon } = isAddMode ? addModeTexts : editModeTexts;

    const { form, formInitialData, setFormData } = useEditForm<AddManualBillFormFields>();

    const vatRateTypeWatch = useWatch(formFields.vatRateType, form) as VatRateType;
    const exemptionReasonWatch = useWatch(formFields.exemptionReason, form) as VatReleaseReason;

    const { displaySuccess } = useSimpleNotification();

    const { data: additionalCostData, isFetching: isAdditionalCostDataFetching } = useAppQuery(
        'ADDITIONAL_COST_DETAILS',
        [childId, additionalCostId],
        () =>
            StaffHTTPService.settlements.getManualBill({
                childId: childId as ChildId,
                additionalCostId: additionalCostId as string,
            }),
        {
            enabled: !isAddMode && !!additionalCostId,
        },
    );

    useEffect(() => {
        if (mode === 'add') {
            form.resetFields();
            return;
        }

        if (additionalCostData) {
            setFormData({
                name: additionalCostData.name,
                costCategory: 'STARTER_KIT' as ManualBillCostCategory,
                vatRateType: additionalCostData.vatRateType,
                issueDate: parseStringIntoDayjsDate(additionalCostData.issueDate),
                dueDate: parseStringIntoDayjsDate(additionalCostData.dueDate),
                saleDate: additionalCostData.saleDate
                    ? parseStringIntoDayjsDate(additionalCostData.saleDate)
                    : null,
                nettAmount: additionalCostData.nettAmount,
                exemptionReason: additionalCostData.exemptionReason,
                customExemptionReason: additionalCostData.customExemptionReason,
                notes: additionalCostData.notes ?? '',
            });
        }
    }, [additionalCostData, form, setFormData, mode]);

    const {
        mutate: addManualBill,
        error: addManualBillError,
        isLoading: isAddManualBillLoading,
    } = useAppMutation(StaffHTTPService.settlements.addManualBill, {
        key: ['ADD_MANUAL_BILL'],
        onSuccess: () => {
            displaySuccess(settlementTexts.children.successfullyAddManualBillMessage);
            onCancel();
        },
        invalidateQueryKeys: [
            ['ADDITIONAL_COST_DETAILS'],
            ['ADDITIONAL_COST_LIST'],
            ['ADDITIONAL_COST'],
            ['CHILD_SETTLEMENT_BALANCE'],
            ['SETTLEMENTS'],
            ['CHILD_SETTLEMENT'],
            ['CHILD_SETTLEMENT_PAYMENT'],
            ['MONTHLY_SETTLEMENTS_V2'],
            ['CHILD_SETTLEMENT_COST_SPLIT'],
        ],
    });

    const {
        mutate: editManualBill,
        error: editManualBillError,
        isLoading: isEditManualBillLoading,
    } = useAppMutation(StaffHTTPService.settlements.editManualBill, {
        key: ['EDIT_MANUAL_BILL'],
        onSuccess: () => {
            displaySuccess(settlementTexts.children.successfullyEditManualBillMessage);
            onCancel();
        },
        invalidateQueryKeys: [
            ['ADDITIONAL_COST_DETAILS'],
            ['ADDITIONAL_COST_LIST'],
            ['ADDITIONAL_COST'],
            ['CHILD_SETTLEMENT_BALANCE'],
            ['SETTLEMENTS'],
            ['CHILD_SETTLEMENT'],
            ['CHILD_SETTLEMENT_PAYMENT'],
            ['MANUAL_BILL', childId, additionalCostId],
        ],
    });

    const onAddManualBillFinish = (billData: AddManualBillFormFields) => {
        const parsedAddEditManualBillData = {
            ...billData,
            costCategory: 'STARTER_KIT' as ManualBillCostCategory,
            childId: childId as ChildId,
            issueDate: billData.issueDate
                ? parseDayjsDateIntoString(billData.issueDate, 'YYYY-MM-DD')
                : null,
            dueDate: billData.dueDate
                ? parseDayjsDateIntoString(billData.dueDate, 'YYYY-MM-DD')
                : null,
            saleDate: billData.saleDate
                ? parseDayjsDateIntoString(billData.saleDate, 'YYYY-MM-DD')
                : null,
        };

        if (isAddMode) {
            addManualBill({
                manualBillAddData: parsedAddEditManualBillData,
            });
        } else {
            editManualBill({
                manualBillEditData: parsedAddEditManualBillData,
                additionalCostId: additionalCostId as string,
                childId: childId as ChildId,
            });
        }
    };

    const billAddError = addManualBillError ? commonTexts.errorMessages.unknown : '';
    const billEditError = editManualBillError ? commonTexts.errorMessages.unknown : '';

    return (
        <AppModal
            width={700}
            open={isOpen}
            onCancel={onCancel}
            isLoading={
                isAddManualBillLoading || isEditManualBillLoading || isAdditionalCostDataFetching
            }
        >
            <AppModalForm<AddManualBillFormFields>
                error={isAddMode ? billAddError : billEditError}
                name="addManualBill"
                form={form}
                onFinish={onAddManualBillFinish}
            >
                <HeaderContainer isFormHeaderAtBottom={false}>
                    <FormHeader title={title} desktopTitleLevel={4} />
                </HeaderContainer>
                <SimpleInputElement
                    name="name"
                    label={settlementTexts.children.addManualBill.name}
                    rules={[max300LengthRule]}
                    placeholder={settlementTexts.children.addManualBill.namePlaceholder}
                />
                <AppFormItem justifyContent="space-between" doubleItem>
                    <DayPeriodPickerElement
                        format="DD/MM/YYYY"
                        mode="start"
                        startField={formFields.issueDate}
                        endField={formFields.dueDate}
                        label={settlementTexts.children.addManualBill.issueDate}
                        isStartValueAfterFinishValueErrorText={
                            settlementTexts.children.addManualBill.issueDateAfterDueDate
                        }
                    />
                    <DayPeriodPickerElement
                        format="DD/MM/YYYY"
                        mode="finish"
                        startField={formFields.issueDate}
                        endField={formFields.dueDate}
                        label={settlementTexts.children.addManualBill.dueDate}
                        isFinishValueBeforeStartValueErrorText={
                            settlementTexts.children.addManualBill.paymentDueDateBeforeIssueDate
                        }
                    />
                    <SimpleDatePicker
                        placeholder="Wybierz datę"
                        format="DD/MM/YYYY"
                        optional
                        hideDateLabel
                        name={formFields.saleDate}
                        formItemLabel={settlementTexts.children.addManualBill.saleDate}
                        disabledDate={(date) => date.isAfter(formFields.dueDate, 'date')}
                    />
                </AppFormItem>
                <SimpleInputElement
                    optional
                    hasOptionalText
                    name="notes"
                    label={settlementTexts.children.addManualBill.notes}
                    rules={[max300LengthRuleOptional]}
                />
                <CurrencyInputElement
                    name={formFields.nettAmount}
                    label={commonTexts.dataLabels.netAmount}
                />
                <VatRatePickerElement
                    name={formFields.vatRateType}
                    label={settlementTexts.children.addManualBill.vatRate}
                    allowClear={false}
                />
                <If condition={vatRateTypeWatch === VatRateType.RELEASED}>
                    <VatReleaseReasonPickerElement
                        name={formFields.exemptionReason}
                        label={settlementTexts.children.addManualBill.exemptionReason}
                        allowClear={false}
                    />
                </If>
                <If condition={exemptionReasonWatch === VatReleaseReason.CUSTOM}>
                    <SimpleInputElement
                        name={formFields.customExemptionReason}
                        label={settlementTexts.children.addManualBill.customExemptionReason}
                        rules={[max300LengthRule]}
                    />
                </If>
                <FormNavButtons
                    formValuesBeforeChanges={formInitialData}
                    buttonText={buttonText}
                    withPlus={isAddMode}
                    leftIcon={buttonIcon}
                />
            </AppModalForm>
        </AppModal>
    );
};
